import { Link } from "react-router-dom";
import { FIRM, OFFICES } from "@/lib/content";
import { Mail, MapPin, Phone, Clock } from "lucide-react";

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative border-t border-white/10 bg-[#0A0A0B] mt-24"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3">
                            <span className="w-10 h-10 border border-[#C4A977]/60 grid place-items-center font-serif text-[#C4A977]">
                                BM
                            </span>
                            <span className="font-serif text-2xl">
                                {FIRM.name}
                            </span>
                        </div>
                        <p className="mt-5 text-[#A1A1AA] max-w-md leading-relaxed">
                            En personlig svensk juristbyrå (enskild firma) med
                            sex kontor runt om i Sverige. Vi kombinerar
                            spetskompetens med personligt engagemang — i varje
                            uppdrag.
                        </p>
                        <p className="mt-6 overline">
                            Org.nr {FIRM.orgNr}  ·  Enskild firma
                        </p>

                        <ul className="mt-8 space-y-4 text-[#A1A1AA] text-sm">
                            <li className="flex items-start gap-3">
                                <Phone size={16} className="text-[#C4A977] mt-0.5" />
                                <a
                                    href={`tel:${FIRM.phoneTel}`}
                                    className="hover:text-[#C4A977] transition-colors"
                                    data-testid="footer-phone"
                                >
                                    {FIRM.phone}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Mail size={16} className="text-[#C4A977] mt-0.5" />
                                <a
                                    href={`mailto:${FIRM.email}`}
                                    className="hover:text-[#C4A977] transition-colors"
                                    data-testid="footer-email"
                                >
                                    {FIRM.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Clock size={16} className="text-[#C4A977] mt-0.5" />
                                <span>{FIRM.hours}</span>
                            </li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <h4 className="text-[#F4F4F5] text-lg mb-5 font-serif">
                            Navigera
                        </h4>
                        <ul className="space-y-3 text-[#A1A1AA] text-sm">
                            <li>
                                <Link to="/om-oss" className="hover:text-[#C4A977] transition-colors">Om oss</Link>
                            </li>
                            <li>
                                <Link to="/tjanster" className="hover:text-[#C4A977] transition-colors">Rättsområden</Link>
                            </li>
                            <li>
                                <Link to="/rattshjalp" className="hover:text-[#C4A977] transition-colors">Rättshjälp & Rättsskydd</Link>
                            </li>
                            <li>
                                <Link to="/paket" className="hover:text-[#C4A977] transition-colors">Paket & fasta priser</Link>
                            </li>
                            <li>
                                <Link to="/medarbetare" className="hover:text-[#C4A977] transition-colors">Medarbetare</Link>
                            </li>
                            <li>
                                <Link to="/orter" className="hover:text-[#C4A977] transition-colors">Våra kontor</Link>
                            </li>
                            <li>
                                <Link to="/nyheter" className="hover:text-[#C4A977] transition-colors">Nyheter</Link>
                            </li>
                            <li>
                                <Link to="/kontakt" className="hover:text-[#C4A977] transition-colors">Kontakt</Link>
                            </li>
                        </ul>

                        <h4 className="text-[#F4F4F5] text-lg mt-10 mb-5 font-serif">
                            Tjänster
                        </h4>
                        <ul className="space-y-3 text-[#A1A1AA] text-sm">
                            <li>
                                <Link
                                    to="/vardnadstvist"
                                    className="hover:text-[#C4A977] transition-colors inline-flex items-center gap-2"
                                    data-testid="footer-link-vardnadstvist"
                                >
                                    Vårdnadstvist
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#C4A977] animate-pulse" />
                                </Link>
                            </li>
                            <li>
                                <Link to="/bodelning" className="hover:text-[#C4A977] transition-colors">Bodelning</Link>
                            </li>
                            <li>
                                <Link to="/testamente" className="hover:text-[#C4A977] transition-colors">Testamente</Link>
                            </li>
                            <li>
                                <Link to="/brottmal" className="hover:text-[#C4A977] transition-colors">Brottmål</Link>
                            </li>
                            <li>
                                <Link to="/arbetsratt-uppsagning" className="hover:text-[#C4A977] transition-colors">Felaktig uppsägning</Link>
                            </li>
                            <li>
                                <Link to="/bostadsratt-tvist" className="hover:text-[#C4A977] transition-colors">Bostadsrättstvist</Link>
                            </li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <h4 className="text-[#F4F4F5] text-lg mb-5 font-serif">
                            Våra kontor
                        </h4>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 text-sm">
                            {OFFICES.map((o) => (
                                <li
                                    key={o.city}
                                    data-testid={`footer-office-${o.city.toLowerCase()}`}
                                    className="text-[#A1A1AA]"
                                >
                                    <div className="flex items-center gap-2 text-[#C4A977]">
                                        <MapPin size={12} />
                                        <span className="text-xs uppercase tracking-widest">
                                            {o.city}
                                        </span>
                                    </div>
                                    <p className="mt-1.5 text-[#F4F4F5]/85 text-[13px] leading-snug">
                                        {o.address}
                                    </p>
                                    <p className="text-[12px] text-[#A1A1AA]/80">
                                        {o.postal}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-[#A1A1AA]">
                    <p>© {new Date().getFullYear()} {FIRM.name}. Alla rättigheter förbehållna.</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                        <Link to="/integritetspolicy" className="hover:text-[#C4A977] transition-colors">Integritetspolicy</Link>
                        <Link to="/allmanna-villkor" className="hover:text-[#C4A977] transition-colors">Allmänna villkor</Link>
                        <Link to="/klagomal" className="hover:text-[#C4A977] transition-colors">Klagomål</Link>
                    </div>
                    <p className="overline">Sverige  ·  6 kontor</p>
                </div>
            </div>
        </footer>
    );
}
